import React, { useEffect, useState } from "react";
import { message } from "antd";
import { ModalComponent, trans } from "@comps/components";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import moment from "moment";
import { getRelatedDataReq, updateDataReq } from "../requests";
import { Commons, Const } from "../../constants";
import CreateStepOne from "./CreateStepOne";
import { attendancePlannerDependencyMap } from "../policyDependencyConfig";

const langKey = Const["attendancePolicyPlanner"].lngKey;

// Edit an existing planner: change or unassign the policies mapped to it. Employee
// mapping is managed separately (Add Employees / View), so this only edits the policy
// slots plus name/date/for_all and saves via update.
const UpdateAttendancePolicyPlanner = (props) => {
  const [, setLoader] = useState(false);
  const [relatedData, setRelatedData] = useState({});
  const [childComponent, setChildComponent] = useState(null);

  const record = props.record || {};

  useEffect(() => {
    getRemoteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRemoteData = () => {
    makeRequestStateless(getRelatedDataReq, null, (response) => setRelatedData(response), null);
  };

  const onDependencyCompleted = (data) => {
    setChildComponent(null);
    if (data && data.length !== 0) {
      getRemoteData();
    }
  };

  const onDependencySuccess = () => {
    getRemoteData();
  };

  const openCreateDependency = (dependencyKey) => {
    const dependency = attendancePlannerDependencyMap[dependencyKey];
    makeRequestStateless(dependency.requestFn, { page: 1 }, (response) => {
      const Component = dependency.createComponent;
      setChildComponent(
        <Component onCompleted={ onDependencyCompleted } onSuccess={ onDependencySuccess } allData={ response } />
      );
    }, onError);
  };

  const openEditDependency = (dependencyKey, selectedId) => {
    if (!selectedId) {
      message.info("Please select a policy first.");
      return;
    }
    const dependency = attendancePlannerDependencyMap[dependencyKey];
    makeRequestStateless(dependency.requestFn, { page: 1 }, (response) => {
      const list = response[dependency.responseKey]?.data || [];
      const relatedList = relatedData[dependency.relatedListKey] || [];
      const found = relatedList.find((item) => item.id === selectedId) || list.find((item) => item.id === selectedId);
      if (!found) {
        message.warning("Selected policy could not be loaded.");
        return;
      }
      const Component = dependency.updateComponent;
      setChildComponent(
        <Component onCompleted={ onDependencyCompleted } allData={ response } record={ found } disabled={ false } />
      );
    }, onError);
  };

  const initialValues = {
    name: record.name,
    date: record.date ? moment(record.date) : null,
    for_all: record.for_all,
    absent_policy_id: record.absent_policy_id ?? null,
    missing_policy_id: record.missing_policy_id ?? null,
    late_arrival_policy_id: record.late_arrival_policy_id ?? null,
    early_left_policy_id: record.early_left_policy_id ?? null,
    short_duration_policy_id: record.short_duration_policy_id ?? null,
    relaxation_policy_id: record.relaxation_policy_id ?? null,
    leave_policy_id: record.leave_policy_id ?? null,
    overtime_policy_id: record.overtime_policy_id ?? null,
    cpl_policy_id: record.cpl_policy_id ?? null,
  };

  const onUpdate = (data) => {
    const obj = {
      ...data,
      id: record.id,
      company_id: record.company_id || (localStorage.getItem("company_id") || 1),
      date: data.date ? moment(data.date).format("YYYY-MM-DD") : null,
    };
    makeRequest(setLoader, updateDataReq, { id: record.id, policy_planer: obj }, onSuccess, onError);
  };

  const onSuccess = (resData) => {
    if (resData.errors !== undefined && resData.errors.length > 0) {
      message.error(`${resData.errors[0].reason}`);
      return;
    }
    message.success("Updated successfully");
    props.onCompleted(resData.policy_planers ? resData.policy_planers.data : []);
  };

  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Something went wrong");
  };

  return (
    <ModalComponent top={ 20 } width={ 1200 } maskClosable={ false }
      mainTitle={ trans(Commons.update) }
      subTitle={ <p style={ { paddingLeft: 75, marginTop: -30 } }>{ trans(`${langKey}.moduleName`) }</p> }
      visible={ true } onCancel={ () => props.onCompleted(false) } footer={ '' }>
      { childComponent }
      <CreateStepOne
        relatedData={ relatedData }
        initialValues={ initialValues }
        response={ onUpdate }
        onCancel={ () => props.onCompleted(false) }
        onCreateDependency={ openCreateDependency }
        onEditDependency={ openEditDependency }
        submitLabel="Update"
      />
    </ModalComponent>
  );
};

export default UpdateAttendancePolicyPlanner;
